import { useMemo, useRef, useState } from 'react';
import { useFloatingList } from './useFloatingList';
import type { Store } from '../data/types';
import { textMatch } from './tableTools';

/**
 * Store combobox: one field — click it and the store list drops down,
 * type and it live-filters by number/name/city/district (~80 stores makes
 * a bare select painful). Same look/mechanics as PrincipalField (the
 * .typeahead styles), but over local data: a store must be PICKED from
 * the list — free text never becomes a value, and blur reverts the text
 * to the current selection. Clearing the text clears the selection
 * (filters treat that as "all stores"). Expects stores pre-sorted.
 */
export function StoreSearchSelect({
  stores,
  value,
  onChange,
  required,
  placeholder = 'All stores',
}: {
  stores: Store[];
  /** Selected store id ('' = none). */
  value: string;
  onChange: (storeId: string) => void;
  required?: boolean;
  /** Shown when nothing is selected (pickers pass something like "Select store…"). */
  placeholder?: string;
}) {
  const selected = stores.find((s) => s.id === value);
  const labelOf = (s: Store) => `${s.storeNumber} — ${s.name}`;

  const [open, setOpen] = useState(false);
  // Only filter once the user has typed since opening — otherwise a field
  // holding "1234 — Main St" would open pre-filtered to one row.
  const [typed, setTyped] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listStyle = useFloatingList(inputRef, open);

  const text = typed !== null ? typed : selected ? labelOf(selected) : '';

  const shown = useMemo(() => {
    const q = (typed ?? '').trim();
    return q
      ? stores.filter((s) =>
          textMatch(q, s.storeNumber, s.name, s.city, s.district),
        )
      : stores;
  }, [stores, typed]);

  const multiDistrict =
    new Set(stores.map((s) => s.district.trim().toLowerCase())).size > 1;

  const pick = (s: Store) => {
    onChange(s.id);
    setTyped(null);
    setOpen(false);
    inputRef.current?.blur();
  };

  const close = () => {
    setOpen(false);
    setTyped(null); // revert text to the selection
  };

  return (
    <span className="typeahead store-search-select">
      <input
        ref={inputRef}
        value={text}
        required={required ?? false}
        placeholder={placeholder}
        autoComplete="off"
        aria-label="Store"
        // Focus alone never opens the list (UI review U36/U37/U39: a dialog focuses its first field,
        // and the list then covered the fields and buttons below). A tap, a click, typing or ↓ opens it.
        onFocus={() => {
          setTyped(null); // a fresh open shows the full list
          // Select-all so typing replaces the current pick immediately.
          setTimeout(() => inputRef.current?.select(), 0);
        }}
        onClick={() => setOpen(true)}
        onBlur={() => setTimeout(close, 150)}
        onChange={(e) => {
          const raw = e.target.value;
          setTyped(raw);
          setOpen(true);
          // Deleting everything is an explicit "no store" (= all stores).
          if (raw.trim() === '' && value !== '') onChange('');
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            setOpen(true);
          } else if (e.key === 'Enter' && open) {
            e.preventDefault(); // never submit the surrounding form from here
            if (shown.length > 0 && typed !== null && typed.trim() !== '') {
              pick(shown[0]!);
            } else {
              close();
            }
          } else if (e.key === 'Escape' && open) {
            // Dismiss the suggestions only — a dialog around the field must not close too (QA 2026-10-26).
            // With the list closed, Escape is the dialog's again.
            e.stopPropagation();
            close();
          }
        }}
      />
      {open && (
        <div className="typeahead-results" style={listStyle}>
          {shown.map((s, i) => (
            <span key={s.id}>
              {multiDistrict &&
                (i === 0 || shown[i - 1]!.district.trim() !== s.district.trim()) && (
                  <div className="typeahead-group muted">
                    {s.district.trim() || 'No district'}
                  </div>
                )}
              <button
                type="button"
                className="typeahead-result"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(s)}
              >
                <span>
                  {labelOf(s)}
                  {s.id === value && ' ✓'}
                </span>
                <span className="muted">{[s.city, s.state].filter(Boolean).join(', ')}</span>
              </button>
            </span>
          ))}
          {shown.length === 0 && (
            <div className="typeahead-status muted">No stores match.</div>
          )}
        </div>
      )}
    </span>
  );
}
